import React from 'react';
import { breadcrumb } from '../utils/diagnostics';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Rendered in place of `children` after an error. Kept minimal on purpose. */
  fallback?: React.ReactNode;
  /** Reported alongside the error so we know which subtree failed. */
  boundaryName: string;
  onError?: (error: Error, componentStack: string) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Contains uncaught render errors to a subtree.
 *
 * This exists for a specific failure mode, not as boilerplate. React unmounts
 * the *entire* tree when a render error reaches the root, which runs every
 * effect's cleanup — including the shoulder-tap poll loop's, which sets
 * `cancelled = true` and clears its timers. The result is a blank frame that
 * has permanently stopped polling, with no network error and nothing in the
 * DOM to suggest why. Because this app usually runs as a hidden iframe, no one
 * sees the blank frame either.
 *
 * Wrapping only the visible UI means a render error there can no longer reach
 * the root, so the polling hook mounted above this boundary keeps running.
 *
 * Note the limits: error boundaries catch errors from render, commit, and
 * lifecycle only. Errors in async callbacks, event handlers, and rejected
 * promises never reach here — those are covered by the global listeners in
 * `installDiagnostics`.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const componentStack = errorInfo.componentStack ?? '';
    breadcrumb('react.errorBoundary', {
      boundary: this.props.boundaryName,
      name: error.name,
      message: error.message,
      stack: error.stack?.slice(0, 1500),
      componentStack: componentStack.slice(0, 1500),
    });
    console.error(`[ErrorBoundary:${this.props.boundaryName}] contained render error`, error);
    this.props.onError?.(error, componentStack);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
